import { ui } from '@procuros/datachecks';

import { logError } from 'services/logging/logging';
import { normalizePath } from 'services/webedi/webedi';
import { GenericError } from 'support/helpers/errors/errors';

import { WebEDIError } from './types';

/**
 * Remaps errors to the first cell in a section if the field doesn't exist.
 * Returns both remapped errors and any hidden errors (errors for sections that don't exist in the UI config).
 */
export const remapErrorsToFirstCell = (
  errors: Record<string, WebEDIError>,
  uiConfig: Array<ui.Section>,
): { remappedErrors: Record<string, WebEDIError>; hiddenErrors: Array<WebEDIError> } => {
  const updatedErrors: Record<string, WebEDIError> = {};
  const hiddenErrors: Array<WebEDIError> = [];

  Object.entries(errors).forEach(([errorKey, errorDetail]) => {
    const [section, rowIndex, ...fieldPath] = errorKey.split('.');
    const joinedFieldPath = fieldPath.join('.');

    // Look for the matching section
    const matchingSection = uiConfig.find((uiConfigSection) => uiConfigSection.type === section);

    if (matchingSection && !rowIndex) {
      // If there's no rowIndex, it means it's a section level error
      // In this case, we don't need to remap the error
      updatedErrors[errorKey] = errorDetail;
      return;
    }

    if (!matchingSection) {
      updatedErrors[errorKey] = { ...errorDetail, type: 'hidden' };
      hiddenErrors.push({ ...errorDetail, type: 'hidden' });
      return;
    }

    const fieldExists = doesSectionContainErrorPath(matchingSection, joinedFieldPath);

    if (fieldExists) {
      // If field exists, keep original key and error detail
      updatedErrors[errorKey] = errorDetail;
      return;
    }

    let newKey: string;

    if (hasFields(matchingSection)) {
      const firstField = matchingSection.fields[0];
      if (!firstField) {
        updatedErrors[errorKey] = errorDetail;
        logError(
          new GenericError(`WebEDI: No fields found in section "${section}". Keeping original error.`, {
            errorDetail,
            errorKey,
          }),
        );
        return;
      }
      newKey = `${section}.${rowIndex}.${firstField.path}`;
    } else if (hasSubSections(matchingSection)) {
      const matchingSubSection = findMatchingSubSection(matchingSection, joinedFieldPath);

      if (!matchingSubSection) {
        updatedErrors[errorKey] = errorDetail;
        logError(
          new GenericError(`WebEDI: No sub-sections found in section "${section}". Keeping original error.`, {
            errorDetail,
            errorKey,
          }),
        );
        return;
      }

      const firstField = getFirstFieldFromSection(matchingSubSection);

      if (!firstField) {
        updatedErrors[errorKey] = errorDetail;
        logError(
          new GenericError(`WebEDI: No fields found in sub-section of "${section}". Keeping original error.`, {
            errorDetail,
            errorKey,
          }),
        );
        return;
      }

      const subSectionPath = normalizeAndCleanPath(matchingSubSection.path);
      const subSectionRowIndex = fieldPath[1] || '0';

      newKey = `${section}.${rowIndex}.${subSectionPath}.${subSectionRowIndex}.${firstField.path}`;
    } else {
      updatedErrors[errorKey] = errorDetail;
      return;
    }

    // Update error detail with new id
    updatedErrors[newKey] = {
      ...errorDetail,
      id: newKey,
    };
    // Old key is automatically dropped since we're not including it in updatedErrors
  });

  return { remappedErrors: updatedErrors, hiddenErrors };
};

const hasSubSections = (section: ui.Section): section is ui.GlobalModificationsSection => {
  return 'sections' in section && Array.isArray((section as ui.GlobalModificationsSection).sections);
};

const hasFields = (
  section: ui.Section,
): section is
  | ui.InfoSection
  | ui.PartiesSection
  | ui.LineItemsSection
  | ui.TransportUnitsSection
  | ui.LocationsSection
  | ui.PaymentTermsSection
  | ui.AttachmentsSection
  | ui.GlobalModificationsSubSection => {
  return 'fields' in section && Array.isArray((section as ui.InfoSection).fields);
};

/**
 * Normalizes a field path and removes wildcard prefixes.
 * @example
 * normalizeAndCleanPath('modification_groups[0]') // => 'modification_groups.0'
 * normalizeAndCleanPath('*.charges[1].amount') // => 'charges.1.amount'
 * normalizeAndCleanPath('*.nested.*.field') // => 'nested.field'
 */
const normalizeAndCleanPath = (path: string): string => {
  return normalizePath(path).replaceAll('*.', '');
};

/**
 * Checks if errorSegments starts with all segments from fieldSegments.
 * @example
 * doesErrorStartWithField(['multi'], ['multi', '0', 'field']) // => true
 * doesErrorStartWithField(['multi', 'nested'], ['multi', 'nested', '0']) // => true
 * doesErrorStartWithField(['multi'], ['other', '0', 'field']) // => false
 * doesErrorStartWithField(['a', 'b', 'c'], ['a', 'b']) // => false (field longer than target)
 */
const doesErrorStartWithField = (fieldSegments: Array<string>, errorSegments: Array<string>): boolean => {
  if (fieldSegments.length > errorSegments.length) {
    return false;
  }

  for (let i = 0; i < fieldSegments.length; i++) {
    if (fieldSegments[i] !== errorSegments[i]) {
      return false;
    }
  }

  return true;
};

/**
 * Recursively checks if a field path exists within a field's structure.
 * Handles both simple fields and multi_column fields with nested structures.
 * @example
 * // Simple field (non-multi_column)
 * doesErrorPathMatchField({ path: 'amount', type: 'text' }, 'amount') // => true
 * doesErrorPathMatchField({ path: 'amount', type: 'text' }, 'description') // => false
 *
 * // Multi-column field with direct match
 * doesErrorPathMatchField({ path: 'multi', type: 'multi_column', type_config: { fields: [...] } }, 'multi.0.subfield') // => true
 *
 * // Multi-column with bracket notation
 * doesErrorPathMatchField({ path: 'modification_groups[0]', type: 'multi_column' }, 'modification_groups.0.charges.0.amount') // => true (if charges.amount exists)
 *
 * // Nested multi-column fields
 * doesErrorPathMatchField({ path: 'level1', type: 'multi_column' }, 'level1.0.level2.0.field') // => true (if level2 multi_column contains field)
 */
const doesErrorPathMatchField = (field: ui.Field, targetPath: string): boolean => {
  const normalizedFieldPath = normalizeAndCleanPath(field.path);

  if (field.type !== ui.FieldTypes.multi_column) {
    return normalizedFieldPath === targetPath;
  }

  return doesErrorPathMatchMultiColumnField(field as ui.MultiColumnField, targetPath, normalizedFieldPath);
};

/**
 * Checks if a target path exists within a multi_column field structure.
 * Handles nested multi_column fields by recursively checking subfields.
 * @example
 * // Direct match with array index
 * doesErrorPathMatchMultiColumnField(multiField, 'multi.0.subfield', 'multi') // => true
 *
 * // Nested multi-column with bracket notation
 * doesErrorPathMatchMultiColumnField(modGroupsField, 'modification_groups.0.charges.0.amount', 'modification_groups.0') // => true
 *
 * // Deeply nested multi-column fields
 * doesErrorPathMatchMultiColumnField(level1Field, 'level1.0.level2.0.field', 'level1') // => true
 */
const doesErrorPathMatchMultiColumnField = (
  field: ui.MultiColumnField,
  targetPath: string,
  normalizedFieldPath: string,
): boolean => {
  const errorSegments = targetPath.split('.');
  const fieldSegments = normalizedFieldPath.split('.');

  if (!doesErrorStartWithField(fieldSegments, errorSegments)) {
    return false;
  }

  let nextSegmentIndex = fieldSegments.length;

  if (nextSegmentIndex < errorSegments.length && !isNaN(Number(errorSegments[nextSegmentIndex]))) {
    nextSegmentIndex++;
  }

  if (nextSegmentIndex >= errorSegments.length) {
    return false;
  }

  const remainingPath = errorSegments.slice(nextSegmentIndex).join('.');

  return field.type_config.fields.some((subField: ui.Field) => {
    const normalizedSubFieldPath = normalizeAndCleanPath(subField.path);

    if (normalizedSubFieldPath === remainingPath) {
      return true;
    }

    if (remainingPath.startsWith(normalizedSubFieldPath + '.')) {
      return doesErrorPathMatchField(subField, remainingPath);
    }

    return false;
  });
};

const getFirstFieldFromSection = (section: ui.Section): ui.Field | undefined => {
  if (hasFields(section)) {
    return section.fields[0];
  }

  if (hasSubSections(section)) {
    const firstSubSection = section.sections[0];
    if (firstSubSection) {
      return getFirstFieldFromSection(firstSubSection);
    }
  }

  return undefined;
};

const findMatchingSubSection = (
  section: ui.GlobalModificationsSection,
  fieldPath: string,
): ui.GlobalModificationsSubSection | undefined => {
  const firstPathSegment = fieldPath.split('.')[0];

  const matchingSubSection = section.sections.find((subSection) => {
    const normalizedPath = normalizeAndCleanPath(subSection.path);
    return normalizedPath === firstPathSegment;
  });

  return matchingSubSection || section.sections[0];
};

const doesSectionContainErrorPath = (section: ui.Section, fieldPath: string): boolean => {
  if (hasFields(section)) {
    return section.fields.some((field) => doesErrorPathMatchField(field, fieldPath));
  }

  if (hasSubSections(section)) {
    const pathSegments = fieldPath.split('.');
    const firstPathSegment = pathSegments[0];
    const matchingSubSection = section.sections.find((subSection) => {
      const normalizedPath = normalizeAndCleanPath(subSection.path);
      return normalizedPath === firstPathSegment;
    });

    if (matchingSubSection) {
      let startIndex = 1;
      if (pathSegments.length > 1 && !isNaN(Number(pathSegments[1]))) {
        startIndex = 2;
      }
      const remainingPath = pathSegments.slice(startIndex).join('.');
      return doesSectionContainErrorPath(matchingSubSection, remainingPath);
    }

    return false;
  }

  return false;
};
